/**
 * @author Vibhor <vibhor.sharma@314ecorp.com>
 * @description Hook for PDF.js
 */
import * as pdfjsLib from 'pdfjs-dist/';
import _ from 'lodash';
import log from 'loglevel';
import { PDFDocumentProxy, PDFPageProxy } from 'pdfjs-dist/types/src/display/api';
import { useCallback, useEffect, useRef, useState } from 'react';

// Set worker path
if (pdfjsLib.GlobalWorkerOptions.workerSrc === '') {
	pdfjsLib.GlobalWorkerOptions.workerSrc = new URL('pdfjs-dist/build/pdf.worker.min.mjs', import.meta.url).toString();
}

interface IPdfProps {
	currentPage: number;
	totalPages: number;
	zoom: number;
	loading: boolean;
	error: string | null;
	loadDocument: (arrayBuffer: ArrayBuffer) => Promise<void>;
	renderPage: (pageIndex: number, zoomLevel?: number) => Promise<HTMLCanvasElement>;
	renderThumbnail: (pageIndex: number) => Promise<string>;
	getPageCount: () => number;
	zoomIn: () => void;
	zoomOut: () => void;
}

const usePdf = (): IPdfProps => {
	const [currentPage, setCurrentPage] = useState(0);
	const [totalPages, setTotalPages] = useState(0);
	const [zoom, setZoom] = useState(1);
	const [loading, setLoading] = useState(false);
	const [error, setError] = useState<string | null>(null);

	const pdfDocument = useRef<PDFDocumentProxy | null>(null);
	const pageCache = useRef<Record<number, PDFPageProxy>>({});

	// Load document
	const loadDocument = useCallback(async (arrayBuffer: ArrayBuffer) => {
		try {
			setLoading(true);
			setError(null);

			// Clear previous document
			if (pdfDocument.current) {
				pdfDocument.current.destroy();
				pdfDocument.current = null;
			}
			pageCache.current = {};

			// Load new document
			const loadingTask = pdfjsLib.getDocument({ data: arrayBuffer });
			pdfDocument.current = await loadingTask.promise;

			setTotalPages(pdfDocument.current.numPages);
			setCurrentPage(0);
		} catch (err) {
			const errorMessage = err instanceof Error ? err.message : 'Failed to load document';
			setError(errorMessage);
			log.error('Failed to load PDF document:', err);
		} finally {
			setLoading(false);
		}
	}, []);

	// Get page from cache or load it
	const getPage = useCallback(async (pageIndex: number): Promise<PDFPageProxy> => {
		if (!pdfDocument.current) {
			throw new Error('Document not loaded');
		}

		if (pageCache.current[pageIndex]) {
			return pageCache.current[pageIndex];
		}

		const page = await pdfDocument.current.getPage(pageIndex + 1); // PDF.js uses 1-based indexing
		pageCache.current[pageIndex] = page;
		return page;
	}, []);

	// Render page to canvas
	const renderPage = useCallback(
		async (pageIndex: number, zoomLevel: number = zoom): Promise<HTMLCanvasElement> => {
			if (!pdfDocument.current) {
				throw new Error('Document not loaded');
			}

			setCurrentPage(pageIndex);
			const page = await getPage(pageIndex);

			const viewport = page.getViewport({ scale: zoomLevel });
			const canvas = document.createElement('canvas');
			const context = canvas.getContext('2d');

			canvas.height = viewport.height;
			canvas.width = viewport.width;

			const renderContext = {
				canvas,
				canvasContext: context,
				viewport,
			};

			await page.render(renderContext).promise;
			return canvas;
		},
		[zoom, getPage],
	);

	const renderThumbnail = useCallback(
		async (pageIndex: number): Promise<string> => {
			if (!pdfDocument.current) {
				throw new Error('Document not loaded');
			}

			const page = await getPage(pageIndex);
			const viewport = page.getViewport({ scale: 0.4 }); // Thumbnail scale

			const canvas = document.createElement('canvas');
			const context = canvas.getContext('2d');

			canvas.height = viewport.height;
			canvas.width = viewport.width;

			const renderContext = {
				canvas,
				canvasContext: context,
				viewport,
			};

			await page.render(renderContext).promise;

			return new Promise((resolve) => {
				canvas.toBlob(
					(blob) => {
						if (blob) {
							resolve(URL.createObjectURL(blob));
						} else {
							resolve('');
						}
					},
					'image/png',
					0.8,
				);
			});
		},
		[getPage],
	);

	const getPageCount = () => totalPages;

	const zoomIn = () => {
		setZoom((prev) => _.clamp(prev + 0.2, 0.5, 3));
	};

	const zoomOut = () => {
		setZoom((prev) => _.clamp(prev - 0.2, 0.5, 3));
	};

	useEffect(() => {
		return () => {
			if (pdfDocument.current) {
				pdfDocument.current.destroy();
			}
			pageCache.current = {};
		};
	}, []);

	return {
		currentPage,
		totalPages,
		zoom,
		loading,
		error,
		loadDocument,
		renderPage,
		renderThumbnail,
		getPageCount,
		zoomIn,
		zoomOut,
	};
};

export default usePdf;
